import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Video, Clock, CheckCircle, XCircle, DollarSign, User } from 'lucide-react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { api } from '../../lib/auth';
import { formatDateTime, cn } from '../../lib/utils';
import { toast } from 'sonner';

export default function AdminAppointments() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadAppointments();
  }, []);

  const loadAppointments = async () => {
    try {
      const response = await api.getExpertSessions();
      setAppointments(response.data);
    } catch (error) {
      console.error('Failed to load appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (id, zoomLink) => {
    if (!zoomLink) {
      toast.error('Please enter a Zoom link');
      return;
    }
    try {
      await api.confirmExpertSession(id, zoomLink);
      toast.success('Session confirmed and client notified');
      loadAppointments();
    } catch (error) {
      toast.error('Failed to confirm session');
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this session?')) return;
    try {
      await api.cancelExpertSession(id);
      toast.success('Session cancelled');
      loadAppointments();
    } catch (error) {
      toast.error('Failed to cancel session');
    }
  };

  const handleMarkPaid = async (id) => {
    try {
      await api.markSessionPaid(id);
      toast.success('Session marked as paid');
      loadAppointments();
    } catch (error) {
      toast.error('Failed to update payment status');
    }
  };

  const filteredAppointments = appointments.filter(a => {
    if (filter === 'all') return true;
    return a.status === filter;
  });

  const stats = {
    total: appointments.length,
    pending: appointments.filter(a => a.status === 'pending').length,
    confirmed: appointments.filter(a => a.status === 'confirmed').length,
    completed: appointments.filter(a => a.status === 'completed').length,
    revenue: appointments.filter(a => a.payment_status === 'paid').reduce((sum, a) => sum + (a.price || 0), 0)
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-heading text-3xl font-bold text-white mb-2">
            Expert Session Appointments
          </h1>
          <p className="text-gray-400">Manage DWY session bookings and send Zoom links</p>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {[
            { label: 'Total', value: stats.total, color: 'text-white' },
            { label: 'Pending', value: stats.pending, color: 'text-yellow-400' },
            { label: 'Confirmed', value: stats.confirmed, color: 'text-blue-400' },
            { label: 'Completed', value: stats.completed, color: 'text-green-400' },
            { label: 'Revenue', value: `$${stats.revenue}`, color: 'text-accent' },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="glass rounded-xl p-4 text-center"
            >
              <p className={cn("text-2xl font-bold", stat.color)}>{stat.value}</p>
              <p className="text-sm text-gray-400">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-6">
          {['all', 'pending', 'confirmed', 'completed', 'cancelled'].map((status) => (
            <Button
              key={status}
              onClick={() => setFilter(status)}
              className={cn(
                "capitalize",
                filter === status ? "btn-primary" : "btn-secondary"
              )}
            >
              {status}
            </Button>
          ))}
        </div>

        {/* Appointments List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass rounded-2xl overflow-hidden"
        >
          {loading ? (
            <div className="p-8 text-center text-gray-400">Loading...</div>
          ) : filteredAppointments.length === 0 ? (
            <div className="p-8 text-center text-gray-400">No appointments found</div>
          ) : (
            <div className="divide-y divide-white/10">
              {filteredAppointments.map((appointment) => (
                <AppointmentRow 
                  key={appointment.id}
                  appointment={appointment}
                  onConfirm={handleConfirm}
                  onCancel={handleCancel}
                  onMarkPaid={handleMarkPaid}
                />
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </DashboardLayout>
  );
}

function AppointmentRow({ appointment, onConfirm, onCancel, onMarkPaid }) {
  const [zoomLink, setZoomLink] = useState(appointment.zoom_link || '');
  const [editing, setEditing] = useState(false);

  return (
    <div className="p-4 hover:bg-white/5 transition-colors">
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={cn(
              "px-2 py-0.5 rounded-full text-xs font-medium",
              appointment.status === 'confirmed' ? "bg-blue-500/20 text-blue-400" :
              appointment.status === 'pending' ? "bg-yellow-500/20 text-yellow-400" :
              appointment.status === 'completed' ? "bg-green-500/20 text-green-400" :
              "bg-red-500/20 text-red-400"
            )}>
              {appointment.status}
            </span>
            <span className={cn(
              "px-2 py-0.5 rounded-full text-xs font-medium",
              appointment.payment_status === 'paid' ? "bg-green-500/20 text-green-400" : "bg-gray-500/20 text-gray-400"
            )}>
              {appointment.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
            </span>
            {appointment.payment_status !== 'paid' && (
              <button
                onClick={() => onMarkPaid(appointment.id)}
                className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors border border-green-500/30"
              >
                Mark Paid
              </button>
            )}
            <span className="text-accent font-semibold">${appointment.price || 0}</span>
          </div>
          <h3 className="font-semibold text-white truncate">{appointment.topic}</h3>
          <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-400">
            <span className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {appointment.client_name || 'Unknown Client'}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {appointment.selected_date}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {appointment.selected_time} ({appointment.session_type})
            </span>
            <span>{appointment.department_name}</span>
          </div>
          {appointment.notes && (
            <p className="text-sm text-gray-500 mt-2 line-clamp-2">{appointment.notes}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {appointment.status === 'pending' && (
            <>
              <Input
                placeholder="Zoom link..."
                value={zoomLink}
                onChange={(e) => setZoomLink(e.target.value)}
                className="input-dark w-48"
              />
              <Button
                onClick={() => onConfirm(appointment.id, zoomLink)}
                className="btn-primary"
                disabled={!zoomLink}
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Confirm
              </Button>
              <Button
                onClick={() => onCancel(appointment.id)}
                className="btn-secondary text-red-400 hover:text-red-300"
              >
                <XCircle className="w-4 h-4" />
              </Button>
            </>
          )}
          {appointment.status === 'confirmed' && appointment.zoom_link && (
            <a 
              href={appointment.zoom_link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:underline text-sm"
            >
              Open Zoom →
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
